import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLingui } from '@lingui/react';
import { useAuthContext } from '@/contexts/AuthContext';
import { MeService } from '@/api/services/MeService';
import type { me_NotificationPreferencesResponse } from '@/api/models/me_NotificationPreferencesResponse';
import { genericErrorMessage } from '@/lib/queryClient';
import { errorToastText } from '@/lib/translateBackendError';
import { logger } from '@/lib/sentry';
import { toastError } from '@/lib/toastError';

export type NotificationPreferences = me_NotificationPreferencesResponse;
export type NotificationPreferenceKey = keyof NotificationPreferences;

export const notificationPreferenceKeys = {
  all: ['me', 'notification-preferences'] as const,
  current: (orgId: string | null | undefined) =>
    [...notificationPreferenceKeys.all, orgId ?? 'none'] as const,
};

/**
 * The caller's push notification preferences (`GET /api/v1/me/notification-preferences`).
 * They govern mobile push only; the web has no push. A user who never saved a
 * preference reads all-true defaults.
 */
export function useNotificationPreferences() {
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: notificationPreferenceKeys.current(orgId),
    queryFn: () => MeService.getApiV1MeNotificationPreferences(),
    enabled: !!isSignedIn && isAuthReady && !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

export interface NotificationPreferenceChange {
  key: NotificationPreferenceKey;
  value: boolean;
}

interface MutationContext {
  queryKey: ReturnType<typeof notificationPreferenceKeys.current>;
  previous: NotificationPreferences | undefined;
}

/**
 * Turns one preference on or off. The PUT carries that one key only (the server
 * accepts partial updates), the row flips at once, and a failed save puts the
 * previous value back and shows an error toast.
 */
export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();
  const { isSignedIn, orgId } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useMutation<NotificationPreferences, unknown, NotificationPreferenceChange, MutationContext>({
    mutationFn: async ({ key, value }) => {
      if (!isSignedIn || !isAuthReady || !orgId) {
        throw new Error('useUpdateNotificationPreference: not signed in to an organisation');
      }
      return MeService.putApiV1MeNotificationPreferences({ [key]: value });
    },
    onMutate: async ({ key, value }) => {
      // Snapshot the org of THIS call so a tenant switch mid-flight cannot write
      // into another org's cache entry.
      const queryKey = notificationPreferenceKeys.current(orgId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationPreferences>(queryKey);
      if (previous) {
        queryClient.setQueryData<NotificationPreferences>(queryKey, { ...previous, [key]: value });
      }
      return { queryKey, previous };
    },
    onError: (error, { key }, context) => {
      if (context?.previous) {
        // Roll back this key only: another toggle may have landed meanwhile.
        const current = queryClient.getQueryData<NotificationPreferences>(context.queryKey);
        queryClient.setQueryData<NotificationPreferences>(context.queryKey, {
          ...(current ?? context.previous),
          [key]: context.previous[key],
        });
      }
      logger.error('Notification preference update failed', {
        context: 'notificationPreferences.update',
        key,
        error,
      });
      toastError(errorToastText(error, i18n, genericErrorMessage));
    },
    onSuccess: (data, _change, context) => {
      queryClient.setQueryData(context.queryKey, data);
    },
  });
}
